/**
 * Redesigned Ticket Detail Page - Modern UX
 *
 * Features:
 * - Chat-style message interface
 * - Enhanced header with visual status
 * - Quick action buttons
 * - Better file attachments display
 * - Timeline-style conversation
 * - Improved reply form
 * - Better mobile responsiveness
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import {
  getTicket,
  addTicketMessage,
  closeTicket,
  reopenTicket,
  uploadTicketAttachment,
} from '@/services/tickets';
import type { Ticket, TicketMessage } from '@/types';

// Status configuration
const statusConfig = {
  open: {
    bg: 'bg-blue-50',
    border: 'border-blue-300',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
    gradient: 'from-blue-500 to-blue-600',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  in_progress: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-300',
    text: 'text-yellow-700',
    dot: 'bg-yellow-500',
    gradient: 'from-yellow-500 to-orange-500',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  waiting_user: {
    bg: 'bg-purple-50',
    border: 'border-purple-300',
    text: 'text-purple-700',
    dot: 'bg-purple-500',
    gradient: 'from-purple-500 to-pink-500',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  resolved: {
    bg: 'bg-green-50',
    border: 'border-green-300',
    text: 'text-green-700',
    dot: 'bg-green-500',
    gradient: 'from-green-500 to-emerald-600',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  closed: {
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    text: 'text-gray-700',
    dot: 'bg-gray-500',
    gradient: 'from-gray-500 to-gray-600',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
};

const priorityConfig = {
  low: { icon: '↓', color: 'text-gray-600', bg: 'bg-gray-100', label: 'Low Priority' },
  medium: { icon: '→', color: 'text-blue-600', bg: 'bg-blue-100', label: 'Medium Priority' },
  high: { icon: '↑', color: 'text-orange-600', bg: 'bg-orange-100', label: 'High Priority' },
  urgent: { icon: '⚡', color: 'text-red-600', bg: 'bg-red-100', label: 'Urgent' },
};

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

// Message Bubble Component
function MessageBubble({ message, isFromCurrentUser, t }: { message: TicketMessage; isFromCurrentUser: boolean; t: any }) {
  const isFromAdmin = message.user?.role === 'admin';

  return (
    <div className={`flex ${isFromCurrentUser ? 'justify-end' : 'justify-start'} mb-6 animate-fade-in`}>
      <div className={`flex gap-3 max-w-2xl ${isFromCurrentUser ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br ${isFromAdmin ? 'from-green-500 to-emerald-600' : isFromCurrentUser ? 'from-indigo-500 to-purple-600' : 'from-blue-500 to-blue-600'} text-white flex items-center justify-center font-bold text-sm shadow-lg`}>
          {message.user?.name?.charAt(0).toUpperCase() || 'U'}
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          {/* Name and Time */}
          <div className={`flex items-center gap-2 mb-1 ${isFromCurrentUser ? 'justify-end' : 'justify-start'}`}>
            <span className="text-sm font-semibold text-gray-900">
              {message.user?.name || 'User'}
            </span>
            {isFromAdmin && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                Support Team
              </span>
            )}
            <span className="text-xs text-gray-500">
              {formatDateTime(message.created_at)}
            </span>
          </div>

          {/* Message Bubble */}
          <div className={`rounded-2xl px-4 py-3 shadow-sm ${
            isFromCurrentUser
              ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
              : isFromAdmin
                ? 'bg-white border-2 border-green-200'
                : 'bg-white border-2 border-gray-200'
          }`}>
            <p className={`text-sm whitespace-pre-wrap ${isFromCurrentUser ? 'text-white' : 'text-gray-800'}`}>
              {message.message}
            </p>

            {/* Attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {message.attachments.map((attachment) => {
                  const isImage = attachment.mime_type?.startsWith('image/');
                  return isImage ? (
                    <button
                      key={attachment.id}
                      onClick={() => setLightboxUrl(attachment.download_url)}
                      className="block mt-1"
                    >
                      <img
                        src={attachment.download_url}
                        alt={attachment.file_name}
                        className="max-h-48 max-w-xs rounded-lg object-cover hover:opacity-90 transition-opacity cursor-zoom-in"
                      />
                    </button>
                  ) : (
                    <a
                      key={attachment.id}
                      href={attachment.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                        isFromCurrentUser
                          ? 'bg-white/20 hover:bg-white/30 text-white'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span className="text-xs font-medium truncate">{attachment.file_name}</span>
                      <span className="text-xs opacity-75">({formatFileSize(attachment.file_size)})</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TicketDetailPage() {
  const { t } = useTranslation('tickets');
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const closeLightbox = useCallback(() => setLightboxUrl(null), []);
  const [isLoading, setIsLoading] = useState(true);
  const [isReplying, setIsReplying] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const currentUserId = user?.id || 1;

  useEffect(() => {
    loadTicket();
  }, [ticketId]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages]);

  const loadTicket = async () => {
    const id = Number(ticketId);
    if (!ticketId || isNaN(id)) {
      navigate('/tickets', { replace: true });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await getTicket(id);
      setTicket(data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('notifications.errorLoading'));
      console.error('Error loading ticket:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket || !replyMessage.trim()) return;

    try {
      setIsReplying(true);
      setError(null);

      const response = await addTicketMessage(ticket.id, { message: replyMessage });
      const messageId = response.message?.id;

      if (selectedFiles.length > 0 && messageId) {
        await Promise.all(
          selectedFiles.map((file) =>
            uploadTicketAttachment(ticket.id, file, messageId).catch(console.error)
          )
        );
      }

      setReplyMessage('');
      setSelectedFiles([]);
      await loadTicket();
    } catch (err: any) {
      setError(err.response?.data?.message || t('notifications.errorUpdating'));
      console.error('Error sending reply:', err);
    } finally {
      setIsReplying(false);
    }
  };

  const handleClose = async () => {
    if (!ticket) return;
    if (!confirm('Are you sure you want to close this ticket?')) return;

    try {
      setIsClosing(true);
      await closeTicket(ticket.id);
      await loadTicket();
    } catch (err: any) {
      setError(err.response?.data?.message || t('notifications.errorUpdating'));
    } finally {
      setIsClosing(false);
    }
  };

  const handleReopen = async () => {
    if (!ticket) return;

    try {
      setIsReopening(true);
      await reopenTicket(ticket.id);
      await loadTicket();
    } catch (err: any) {
      setError(err.response?.data?.message || t('notifications.errorUpdating'));
    } finally {
      setIsReopening(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="bg-white rounded-2xl p-8 space-y-4">
              <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading Ticket</h2>
            <p className="text-red-700 mb-6">{error || t('notifications.errorLoading')}</p>
            <Link to="/tickets" className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('detail.backToList')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  const status = statusConfig[ticket.status as keyof typeof statusConfig];
  const priority = priorityConfig[ticket.priority as keyof typeof priorityConfig];
  const messages = ticket.messages?.filter((msg) => !msg.is_internal_note) || [];

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <Link
            to="/tickets"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Tickets
          </Link>

          {/* Ticket Info */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              {/* Ticket Number and Status */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm font-mono font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {ticket.ticket_number}
                </span>
                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full ${status.bg} ${status.border} border-2`}>
                  <span className={`w-2 h-2 rounded-full ${status.dot} animate-pulse`}></span>
                  <span className={`text-sm font-semibold ${status.text}`}>
                    {t(`status.${ticket.status}`)}
                  </span>
                </div>
                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${priority.bg}`}>
                  <span className={`text-lg ${priority.color}`}>{priority.icon}</span>
                  <span className={`text-sm font-semibold ${priority.color}`}>
                    {priority.label}
                  </span>
                </div>
              </div>

              {/* Subject */}
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{ticket.subject}</h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  {t(`category.${ticket.category}`)}
                </span>

                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {new Date(ticket.created_at).toLocaleDateString()}
                </span>

                {ticket.assignedAdmin && (
                  <span className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white flex items-center justify-center text-xs font-bold">
                      {ticket.assignedAdmin.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium">Assigned to {ticket.assignedAdmin.name}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {ticket.status !== 'closed' && ticket.status !== 'resolved' ? (
                <button
                  onClick={handleClose}
                  disabled={isClosing}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {isClosing ? 'Closing...' : t('actions.close')}
                </button>
              ) : (
                <button
                  onClick={handleReopen}
                  disabled={isReopening}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {isReopening ? 'Reopening...' : t('actions.reopen')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Messages Container */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              Conversation ({messages.length} {messages.length === 1 ? 'message' : 'messages'})
            </h2>
          </div>

          {/* Messages */}
          <div className="p-6 max-h-[600px] overflow-y-auto">
            {messages.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p>No messages yet. Start the conversation below.</p>
              </div>
            ) : (
              <div>
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    isFromCurrentUser={message.user_id === currentUserId}
                    t={t}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Reply Form */}
        {ticket.status !== 'closed' && ticket.status !== 'resolved' ? (
          <form onSubmit={handleReply} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              Reply to this ticket
            </h3>

            <textarea
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={5}
              disabled={isReplying}
            />

            {/* File Upload */}
            <div className="mt-4">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleFileChange}
                multiple
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip"
                disabled={isReplying}
              />
              <label
                htmlFor="file-upload"
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Attach Files
              </label>
            </div>

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 truncate">{file.name}</span>
                      <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      disabled={isReplying}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Submit Button */}
            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={isReplying || !replyMessage.trim()}
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                {isReplying ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-8 text-center">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <p className="text-gray-600 font-medium">
              This ticket is {ticket.status}. Reopen it to continue the conversation.
            </p>
          </div>
        )}
      </div>
    </div>

    {/* Image lightbox */}
    {lightboxUrl && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={closeLightbox}
      >
        <div className="relative max-w-5xl max-h-screen p-4" onClick={e => e.stopPropagation()}>
          <button
            onClick={closeLightbox}
            className="absolute -top-2 -right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white text-gray-800 shadow-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxUrl}
            alt="Attachment"
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain"
          />
          <a
            href={lightboxUrl}
            download
            onClick={e => e.stopPropagation()}
            className="mt-3 flex items-center justify-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </a>
        </div>
      </div>
    )}
    </>
  );
}
